import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import Banner from '../Components/Banner';
import { ChevronRight } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import LocationForm from '../Components/mumbai/LocationForm';
import ServiceNowLeadForm from '../Components/ServiceNowLeadForm';

function ServiceNowImplementationPartner() {
const servicenowFaqData = [
    {
        id: 1,
        question: "What does a ServiceNow implementation partner do?",
        answer:
            "A ServiceNow implementation partner helps plan, configure, customize, integrate, test, and deploy ServiceNow based on your business and technical requirements."
    },
    {
        id: 2,
        question: "Why do I need a ServiceNow implementation partner?",
        answer:
            "An implementation partner helps align ServiceNow with your business processes while managing configuration, customization, integrations, testing, and deployment."
    },
    {
        id: 3,
        question: "Which ServiceNow modules can you implement?",
        answer:
            "Mobikode supports ITSM, ITOM, CMDB, CSM, HRSD, integrations, workflow automation, custom applications, and other ServiceNow capabilities."
    },
    {
        id: 4,
        question: "How long does a ServiceNow implementation take?",
        answer:
            "The timeline depends on project scope, modules, integrations, customization, data requirements, testing, and deployment needs."
    },
    {
        id: 5,
        question: "Can ServiceNow be integrated with existing systems?",
        answer:
            "Yes. ServiceNow can integrate with enterprise and third-party systems using REST APIs, SOAP APIs, IntegrationHub, data synchronization, and automated workflows."
    },
    {
        id: 6,
        question: "Why choose Mobikode as your ServiceNow implementation partner?",
        answer:
            "Mobikode supports ServiceNow planning, configuration, customization, integrations, automation, testing, and deployment based on your project requirements."
    }
];
    
          const [activeIndex, setActiveIndex] = useState(0);
          const [faqActiveIndex, setFaqActiveIndex] = useState(0);
    
    const toggleFaqAccordion = (index) => {
        setFaqActiveIndex(faqActiveIndex === index ? -1 : index);
    };
  return (
    
    <div>
<Helmet>
    {/* Primary SEO */}
    <title>
        ServiceNow Implementation Partner | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode provides ServiceNow implementation services for businesses, including planning, configuration, customization, integrations, workflow automation, and platform deployment."
    />

    <meta
        name="keywords"
        content="ServiceNow implementation partner, ServiceNow implementation services, ServiceNow implementation company, ServiceNow implementation consultant, ServiceNow implementation cost, ServiceNow implementation timeline, ServiceNow ITSM implementation, ServiceNow ITOM implementation, ServiceNow CMDB implementation, ServiceNow CSM implementation, ServiceNow HRSD implementation, ServiceNow integration services"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/servicenow-implementation-partner/"
    />


    {/* Open Graph */}
    <meta
        property="og:title"
        content="ServiceNow Implementation Partner | Mobikode"
    />

    <meta
        property="og:description"
        content="Partner with Mobikode for ServiceNow implementation services covering planning, configuration, customization, integrations, workflow automation, and platform deployment."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/servicenow-implementation-partner/"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:site_name"
        content="Mobikode"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/services/servicenow-implementation-partner.jpg"
    />

    <meta
        property="og:image:alt"
        content="ServiceNow Implementation Partner by Mobikode"
    />


    {/* Twitter / X */}
    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="ServiceNow Implementation Partner | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Mobikode provides ServiceNow implementation services for ITSM, ITOM, CMDB, CSM, HRSD, integrations, workflow automation, and custom platform requirements."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/services/servicenow-implementation-partner.jpg"
    />

    <meta
        name="twitter:image:alt"
        content="ServiceNow Implementation Partner by Mobikode"
    />
</Helmet>


<section className="relative h-[500px] lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">
    <div className="absolute inset-0 z-0">
        <img
            src="/assets/services/bg3.png"
            alt="ServiceNow implementation partner for enterprise ServiceNow projects"
            className="h-full w-full object-cover opacity-40"
        />

        <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />
    </div>

    <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">

        {/* Hero Heading */}
        <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[115px] mt-[40px]"
        >
            <h1 className="text-[38px] lg:text-[55px] text-[#FFFFFF] max-w-[800px] w-full font-sora font-[600] leading-[1.2] mb-[22px]">
                ServiceNow Implementation Partner
            </h1>

            <p className="text-[#FFFFFF] font-graphik text-[16px] md:text-[20px] font-[400] leading-[1.6] mb-4">
                ServiceNow Implementation Services for Enterprise Organizations
            </p>
        </motion.div>

        {/* Hero Description */}
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left md:mt-0"
        >
            <p className="text-[#FFFFFF] font-graphik text-[14px] md:text-[16px] font-[400] leading-[1.6]">
                Partner with Mobikode for ServiceNow implementation services
                designed around your business processes, technical requirements,
                and platform goals. We support ServiceNow implementations across
                ITSM, ITOM, CMDB, CSM, HRSD, integrations, workflow automation,
                and custom platform requirements.
            </p>
        </motion.div>

    </div>
</section>


<section className="bg-[#FFFFFF] overflow-hidden lg:py-[100px] py-[60px]">
    <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">

        <div className="flex flex-col lg:flex-row items-center gap-[40px] lg:gap-[80px]">

            {/* LEFT IMAGE */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="w-full lg:w-1/2"
            >
                <div className="w-full h-[280px] md:h-[380px] lg:h-[450px] overflow-hidden">
                    <img
                        src="/assets/sales/img2.png"
                        alt="ServiceNow implementation partner for enterprise implementation services"
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                </div>
            </motion.div>

            {/* RIGHT CONTENT */}
            <div className="w-full lg:w-1/2 text-left">

                {/* Service Overview Label */}
                <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-[10px] pb-[15px] lg:pb-[20px]"
                >
                    <motion.span
                        className="font-[500] font-graphik text-[16px] lg:text-[20px] leading-[1.6] text-[#01386A]"
                    >
                        Service Overview
                    </motion.span>
                </motion.div>

                {/* Title */}
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="font-[600] font-sora lg:text-[30px] max-w-[850px] w-full text-[20px] leading-[1.3] text-[#01386A]"
                >
                    ServiceNow Implementation Services for Your Business
                </motion.h2>

                {/* Description */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.35 }}
                    className="font-[400] max-w-[800px] w-full font-graphik text-[14px] lg:text-[16px] leading-[1.6] lg:leading-[22px] text-[#01386A] mt-[20px] lg:mt-[25px]"
                >
                    As a ServiceNow implementation partner, Mobikode helps
                    businesses plan, configure, customize, and implement
                    ServiceNow solutions around their operational and technical
                    requirements. Our implementation services support ServiceNow
                    ITSM, ITOM, CMDB, CSM, HRSD, integrations, workflow
                    automation, and custom platform requirements.

                    <br /><br />

                    From implementation planning and platform configuration to
                    custom development, integrations, testing, and deployment,
                    our team can support the different stages of your ServiceNow
                    implementation. We work with your existing teams to align
                    ServiceNow capabilities with business processes, user
                    requirements, and long-term platform goals.
                </motion.p>

                {/* Bottom Accent */}
                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "70px" }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="h-[3px] bg-[#ED6800] mt-[25px] lg:mt-[35px]"
                />

            </div>

        </div>

    </div>
</section>

<section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

        {/* Main Flex Container */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">

            {/* Left Side: Title Section */}
            <div className="w-full lg:w-[40%] lg:sticky lg:top-24">
                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                    ServiceNow Implementation Expertise
                </h2>
            </div>


            {/* Right Side: Cards List Section */}
            <div className="w-full lg:w-[60%] flex flex-col gap-6">

                {/* Card 1 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Implementation Planning
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Plan your ServiceNow implementation around business
                        processes, technical requirements, platform goals, and
                        project scope. Our team can help define implementation
                        priorities, workflows, integrations, and delivery
                        requirements.
                    </p>
                </div>


                {/* Card 2 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow ITSM Implementation
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Implement ServiceNow ITSM capabilities for incident,
                        problem, change, request, and knowledge management.
                        Configure workflows and platform functionality around
                        your organization's IT service delivery processes.
                    </p>
                </div>


                {/* Card 3 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow ITOM & CMDB Implementation
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Implement ITOM and CMDB capabilities to improve
                        infrastructure visibility, configuration management,
                        discovery, service mapping, and operational workflows
                        across your ServiceNow environment.
                    </p>
                </div>


                {/* Card 4 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow CSM & HRSD Implementation
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Configure ServiceNow CSM and HRSD solutions to support
                        customer service and employee service workflows, case
                        management, portals, automation, and service delivery
                        requirements.
                    </p>
                </div>


                {/* Card 5 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        ServiceNow Integration Implementation
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Connect ServiceNow with enterprise applications using
                        REST and SOAP APIs, IntegrationHub, third-party systems,
                        data synchronization, and automated workflows as part
                        of your implementation.
                    </p>
                </div>


                {/* Card 6 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Custom ServiceNow Implementation
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Customize ServiceNow to meet specific business
                        requirements through custom applications, workflows,
                        scripting, automation, platform configuration, and
                        tailored functionality across your implementation.
                    </p>
                </div>

            </div>

        </div>

    </div>
</section>


<section className="w-full bg-white py-16 lg:py-24">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

        <h2 className="text-[#01386A] text-[20px] md:text-[30px] font-[600] font-sora mb-6 lg:mb-10 text-left">
            ServiceNow Implementation Expertise
        </h2>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">

            {/* LEFT SIDE - TABS */}
            <div className="lg:w-1/2 flex flex-col">

                {/* Implementation Planning */}
                <div
                    onClick={() => setActiveIndex(0)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 0
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 0
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Implementation Planning
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 0
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* ITSM */}
                <div
                    onClick={() => setActiveIndex(1)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 1
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 1
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow ITSM Implementation
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 1
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* ITOM & CMDB */}
                <div
                    onClick={() => setActiveIndex(2)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 2
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 2
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow ITOM & CMDB Implementation
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 2
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* CSM */}
                <div
                    onClick={() => setActiveIndex(3)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 3
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 3
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow CSM & HRSD Implementation
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 3
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* Integrations */}
                <div
                    onClick={() => setActiveIndex(4)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 4
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 4
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        ServiceNow Integration Implementation
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 4
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>


                {/* Custom Implementation */}
                <div
                    onClick={() => setActiveIndex(5)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 5
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 5
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Custom ServiceNow Implementation
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 5
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

            </div>


            {/* RIGHT SIDE - CONTENT */}
            <div className="lg:w-1/2 flex flex-col justify-start">

                <div className="min-h-[100px] mb-8">

                    {/* Implementation Planning */}
                    {activeIndex === 0 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Plan your ServiceNow implementation around your
                            business processes, technical requirements, platform
                            goals, and project scope. Our team can help define
                            implementation priorities, workflows, integrations,
                            configuration requirements, and delivery milestones.
                        </p>
                    )}


                    {/* ITSM */}
                    {activeIndex === 1 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Implement ServiceNow ITSM capabilities for incident,
                            problem, change, request, and knowledge management.
                            Our team can configure workflows, automate service
                            processes, and align ServiceNow IT service management
                            capabilities with your organizational requirements.
                        </p>
                    )}


                    {/* ITOM & CMDB */}
                    {activeIndex === 2 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Implement ServiceNow ITOM and CMDB capabilities to
                            improve infrastructure visibility, configuration
                            management, discovery, service mapping, and operational
                            workflows. Our implementation approach can support
                            complex IT operations and configuration requirements.
                        </p>
                    )}


                    {/* CSM & HRSD */}
                    {activeIndex === 3 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Implement ServiceNow CSM and HRSD solutions to support
                            customer service and employee service workflows. Our
                            team can configure case management, portals,
                            automation, employee services, and platform
                            integrations according to your business processes.
                        </p>
                    )}


                    {/* Integrations */}
                    {activeIndex === 4 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Connect ServiceNow with enterprise applications using
                            REST and SOAP APIs, IntegrationHub, third-party
                            systems, data synchronization, and automated workflows.
                            Our implementation team can support integration
                            planning, configuration, testing, and deployment.
                        </p>
                    )}


                    {/* Custom Implementation */}
                    {activeIndex === 5 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Customize ServiceNow around your specific business
                            requirements through custom applications, workflows,
                            scripting, automation, platform configuration, and
                            tailored functionality. Our team can support custom
                            ServiceNow implementation requirements from planning
                            through deployment.
                        </p>
                    )}

                </div>


                {/* IMAGE */}
                <div className="w-full h-[300px] lg:h-[378px] overflow-hidden relative">

                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/50 to-transparent z-10 pointer-events-none"></div>


                    {activeIndex === 0 && (
                        <img
                            src="/assets/home-/ai-2.png"
                            alt="ServiceNow implementation planning services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {activeIndex === 1 && (
                        <img
                            src="/assets/home-/ai-3.png"
                            alt="ServiceNow ITSM implementation services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {activeIndex === 2 && (
                        <img
                            src="/assets/home-/ai-4.png"
                            alt="ServiceNow ITOM and CMDB implementation services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {activeIndex === 3 && (
                        <img
                            src="/assets/home-/img1.png"
                            alt="ServiceNow CSM and HRSD implementation services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {activeIndex === 4 && (
                        <img
                            src="/assets/home-/img2.png"
                            alt="ServiceNow integration implementation services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}


                    {activeIndex === 5 && (
                        <img
                            src="/assets/home-/img3.png"
                            alt="Custom ServiceNow implementation services"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                </div>

            </div>

        </div>
    </div>


    <style>{`
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(10px);
            }

            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
        }
    `}</style>

</section>

<section className="w-full pb-[40px] md:pb-[100px] bg-white">
    <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

        <div className="flex flex-col lg:flex-row items-start gap-[24px] md:gap-[40px] lg:gap-[80px]">

            {/* LEFT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                    duration: 0.8,
                    ease: "easeOut"
                }}
                className="w-full lg:w-5/12 lg:sticky lg:top-[120px]"
            >
                <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight leading-[1.2] lg:max-w-[350px]">
                    Frequently Asked Questions About ServiceNow Staff Augmentation
                </h2>
            </motion.div>


            {/* RIGHT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{
                    once: true,
                    amount: 0.1
                }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="w-full lg:w-7/12 flex flex-col border border-[#E4E8F5] bg-white rounded-sm"
            >

                {servicenowFaqData.map((faq, index) => {

                    const isActive = faqActiveIndex === index;

                    return (
                        <div
                            key={faq.id}
                            className={`flex flex-col border-b ${
                                isActive
                                    ? "border-[#ED6800] border-b-[3px] md:border-b-[4px]"
                                    : "border-[#E4E8F5]"
                            } last:border-b-0 transition-colors duration-300`}
                        >

                            {/* QUESTION */}
                            <button
                                type="button"
                                onClick={() => toggleFaqAccordion(index)}
                                className="w-full flex justify-between items-center text-left py-[18px] sm:py-[24px] md:py-[32px] px-[16px] sm:px-[24px] md:px-[40px] group cursor-pointer bg-white md:hover:bg-[#F8FAFF] transition-colors duration-300"
                                aria-expanded={isActive}
                            >

                                <h3
                                    className={`font-sora max-w-[85%] text-[16px] md:text-[20px] font-[600] pr-[16px] sm:pr-[20px] transition-colors duration-300 ${
                                        isActive
                                            ? "text-[#01386A]"
                                            : "text-[#01386A] md:group-hover:text-[#ED6800]"
                                    }`}
                                >
                                    {faq.question}
                                </h3>


                                {/* PLUS / MINUS ICON */}
                                <div className="flex-shrink-0 text-[#01386A] transition-colors duration-300 md:group-hover:text-[#ED6800]">

                                    {isActive ? (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    ) : (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="12"
                                                y1="5"
                                                x2="12"
                                                y2="19"
                                            />

                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    )}

                                </div>

                            </button>


                            {/* ANSWER */}
                            <AnimatePresence initial={false}>

                                {isActive && (

                                    <motion.div
                                        initial={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        animate={{
                                            height: "auto",
                                            opacity: 1
                                        }}
                                        exit={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        transition={{
                                            duration: 0.4,
                                            ease: "easeInOut"
                                        }}
                                        className="overflow-hidden bg-white"
                                    >

                                        <div className="pb-[20px] sm:pb-[32px] px-[16px] sm:px-[24px] md:px-[40px]">

                                            <p className="font-graphik text-[#6C7689] text-[14px] md:text-[16px] font-[500] leading-[1.6]">
                                                {faq.answer}
                                            </p>

                                        </div>

                                    </motion.div>

                                )}

                            </AnimatePresence>

                        </div>
                    );
                })}

            </motion.div>

        </div>

    </div>
</section>

 <ServiceNowLeadForm/>
<Banner
    title="ServiceNow Implementation Partner"
    text="Implement ServiceNow with a trusted partner for planning, configuration, customization, integrations, workflow automation, and scalable platform deployment aligned with your business requirements."
/>

    </div>
  )
}

export default ServiceNowImplementationPartner
